import { useCallback, useEffect, useState } from "react";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Spinner } from "./components/ui";
import { AssistantBubble } from "./components/AssistantBubble";
import { AccountPage } from "./pages/Account";
import { ClubPage } from "./pages/Club";
import { ConversationPage } from "./pages/Conversation";
import { DeleteConfirmPage } from "./pages/DeleteConfirm";
import { HomePage } from "./pages/Home";
import { LinkOpenerPage } from "./pages/LinkOpener";
import { NoBrandPage, NotFoundPage } from "./pages/Misc";
import { PrivacyPage } from "./pages/Privacy";
import { ProposalPage } from "./pages/Proposal";
import { ResidentsPage } from "./pages/Residents";
import { SignInPage } from "./pages/SignIn";
import { StaffPage } from "./pages/Staff";
import { TripPage } from "./pages/Trip";
import { interceptLink, matchRoute, usePath } from "./router";
import { useSession } from "./session";

export function App() {
  const path = usePath();
  const route = matchRoute(path);
  const { brandInfo, brand, me, loadError, refresh, t } = useSession();
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>(undefined);
  const onSignInEmail = useCallback((email: string) => setPrefillEmail(email), []);

  useEffect(() => {
    document.addEventListener("click", interceptLink);
    return () => document.removeEventListener("click", interceptLink);
  }, []);

  // The staff page lives on the API host: no brand, no customer session.
  if (route.name === "staff") return <StaffPage />;

  if (brandInfo === undefined || me === undefined) {
    return (
      <div className="page page-center">
        <Spinner />
      </div>
    );
  }

  if (loadError && !brand) {
    return (
      <div className="page page-center">
        <main className="centered">
          <h1>{t("appName")}</h1>
          <p className="muted">{t("somethingWrong")}</p>
          <button type="button" className="button button-primary" onClick={() => void refresh()}>
            {t("retry")}
          </button>
        </main>
      </div>
    );
  }

  if (!brand) return <NoBrandPage />;

  if (route.name === "link") return <LinkOpenerPage token={route.token} onSignInEmail={onSignInEmail} />;
  if (route.name === "signin") {
    if (me && me.session.kind === "full" && !route.magicToken) return <Redirect to="/" />;
    return <SignInPage magicToken={route.magicToken} prefillEmail={prefillEmail} />;
  }
  if (route.name === "notFound") return <NotFoundPage />;
  // The deletion link from the email works signed out as well: the API checks the token, not the session.
  if (route.name === "deleteConfirm") return <DeleteConfirmPage token={route.token} />;
  // The privacy notice is public: signed out it stands alone with the footer, signed in it sits in the shell.
  if (route.name === "privacy" && !me) {
    return (
      <div className="shell">
        <PrivacyPage />
        <Footer />
      </div>
    );
  }

  // The proposal holder (and a resident opening the section) signs in and comes back.
  if (!me) return <SignInPage prefillEmail={prefillEmail} returnTo={route.name === "proposal" || route.name === "residents" ? path : undefined} />;

  return (
    <div className="shell">
      <Header path={path} />
      {route.name === "home" && <HomePage />}
      {route.name === "trip" && <TripPage id={route.id} />}
      {route.name === "proposal" && <ProposalPage id={route.id} />}
      {route.name === "account" && <AccountPage />}
      {route.name === "club" && <ClubPage />}
      {route.name === "conversation" && <ConversationPage />}
      {route.name === "residents" && <ResidentsPage />}
      {route.name === "privacy" && <PrivacyPage />}
      <Footer />
      {(route.name === "home" || route.name === "trip") && <AssistantBubble />}
    </div>
  );
}

function Redirect({ to }: { to: string }) {
  useEffect(() => {
    window.history.replaceState(null, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [to]);
  return null;
}
